import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { fnGetWhLocLotCtPltInv, fnGetWhSnInv } from "@shared/utils/InvUtils";
import { getInitFormData, getAutoSlip, CalAm } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { fnGetIoFgTrns } from "@shared/utils/DrilldownUtils";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, ExecuteRunner } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";
import SnCodeBox from "@shared/components/SnCodeBox";
import SnLabel from "@shared/components/SnLabel";
import SnDateBox from "@shared/components/SnDateBox";
import SnButton from "@shared/components/SnButton";
import SnNumberBox from "@shared/components/SnNumberBox";
import { openAddInfoPopup } from "@widgets/commonpopup/sys/openAddInfoPopup";
import {
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";

interface Props {
  Menuprops: menuInfo;
}

interface CodeValue {
  code: string;
  name: string;
}

interface TabState {
  page: string;
  searchParams: any | null;
}

const SN_MTL_ISU_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
    grid5: React.createRef<SnGridRef>(),
    grid6: React.createRef<SnGridRef>(),
    grid7: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);

  const txtBarcodeRef = useRef<any>(null);
  const numberBoxFifoRef = useRef<any>(null);

  const isFirstRender = useRef(true); // 화면 오픈시 조회 방지

  // 처리 후 '처리' 탭으로 이동할 때 조회조건의 출하일을 오늘로 덮어쓰므로,
  // 덮어쓰기 직전의 '미처리' 조회조건을 보관했다가 미처리 탭으로 돌아올 때 되돌린다.
  const prevSearchParams = useRef<any>(null);

  const { userInfo, config } = global(); // Login user 정보
  //#endregion

  // 선택 재고리스트
  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);

  // 스위치
  const switchRef = useRef<SnSwitchHandle>(null);

  // 처리 컴포넌트
  const [dateBox, setDateBoxValue] = useState<string>(dateToString(new Date()));
  const [codeWh, setWhCode] = useState<CodeValue>({ code: "", name: "" });
  const [codeEmp, setEmpCode] = useState<CodeValue>({
    code: userInfo.cd_emp || "",
    name: userInfo.nm_emp || "",
  });
  const [whReadOnly, setwhReadOnly] = useState<boolean>(false);

  const [tabPageState, setTabState] = useState<TabState>({
    page: "1",
    searchParams: null,
  });

  const dateValueChanged = async (value: string) => {
    setDateBoxValue(value);
  };
  const whValueChanged = async (code: string, name: string, rowData?: any) => {
    setWhCode({ code: code, name: name });
    // 출고창고가 선택돼있으면 해당 창고로 재고 조회
    if (code !== "") {
      getInv(code);
    } else {
      setDataSources((prev: any) => ({ ...prev, grid3: [] }));
    }
  };
  const empValueChanged = async (code: string, name: string, rowData?: any) => {
    setEmpCode({ code: code, name: name });
  };

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
    grid5: [],
    grid6: [],
    grid7: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid5: [],
    grid6: [],
    grid7: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [addInfoFlags, setAddInfoFlags] = useState<string[]>([]);
  const [addInfoFlags2, setAddInfoFlags2] = useState<string[]>([]);
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
          grid5: rtn.grid.grid5,
          grid6: rtn.grid.grid6,
          grid7: rtn.grid.grid7,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const drilldownHandledRef = useRef<any>(null);
  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      // 조회·팝업 등으로 컬럼이 다시 만들어질 때마다 드릴다운이 재실행되지 않게(처리탭으로 되돌아가는 문제) 드릴다운 1건당 1번만.
      if (drilldownHandledRef.current !== Menuprops) {
        drilldownHandledRef.current = Menuprops;
        onActivate(Menuprops.nodeData);
      }
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (tabPageState.page) {
      if (!tabPageState.searchParams) {
        onSearch(null);
      } else {
        onSearch(tabPageState.searchParams);
      }
    }
  }, [tabPageState.page]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onExecute,
    onPrint,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      if (tabPageState.page === "1") {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_isu_reg_004_01",
          params: searchDataToUse,
        };

        resetCondition();

        getGridCurrent("grid1").resetUpdate();
        getGridCurrent("grid2").resetUpdate();
        getGridCurrent("grid3").resetUpdate();
        const data: any = await fnGetDataSet(params);

        const flags =
          addInfoFlags.length > 0
            ? addInfoFlags
            : await loadAddInfoFlags("grid2", setAddInfoFlags);

        setDataSources({
          grid1: data[0],
          grid2: hydrateRowsWithAddInfo(data[1] || [], flags),
          grid3: data[2] || [],
          grid4: [],
          grid5: [],
          grid6: [],
          grid7: [],
        });
      } else {
        const searchDataToUse = searchData || searchRef.current.getFormdata();
        const params = {
          id: "sn_mtl_isu_reg_004_02",
          params: searchDataToUse,
        };

        const flags2 =
          addInfoFlags2.length > 0
            ? addInfoFlags2
            : await loadAddInfoFlags("grid6", setAddInfoFlags2);

        getGridCurrent("grid5").resetUpdate();
        getGridCurrent("grid6").resetUpdate();
        getGridCurrent("grid7").resetUpdate();
        const data: any = await fnGetDataSet(params);

        setDataSources({
          grid1: [],
          grid2: [],
          grid3: [],
          grid4: [],
          grid5: data[0],
          grid6: hydrateRowsWithAddInfo(data[1] || [], flags2),

          grid7: data[2] || [],
        });
      }
    } catch (error: any) {}
  };
  //#endregion

  //#region onExecute
  const onExecute = async (id: string) => {
    let hasInvalid = false;

    try {
      switch (id) {
        case "Save": // 처리
          {
            const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            if (dateBox === "") {
              await showDialog("alter", "출하일을 입력해 주세요.");
              return false;
            }

            if (codeEmp.code === "") {
              await showDialog("alter", "담당자를 입력해 주세요.");
              return false;
            }

            let hasInvalidcdItem = "";

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              if (node.data.qt !== node.data.qt_isu) {
                if (!hasInvalid) {
                  hasInvalid = true;
                  hasInvalidcdItem = node.data.cd_item;
                }
              }
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "품목코드 [" +
                  hasInvalidcdItem +
                  "]의 의뢰수량과 출하수량이 같지 않습니다."
              );
              return false;
            }

            const qtioH: any[] = [];
            const qtio: any[] = [];
            const qtioLotSn: any[] = [];

            const insertDataH = {
              cd_firm: userInfo.cd_firm,
              no_io: "",
              cd_biz: focusedRowNodeH.data.cd_biz,
              cd_wh: codeWh.code ? codeWh.code : "",
              dt_io: dateBox,
              cd_cust: focusedRowNodeH.data.cd_cust,
              cd_cust_dlv: focusedRowNodeH.data.cd_cust_dlv,
              cd_exch: focusedRowNodeH.data.cd_exch,
              rt_exch: focusedRowNodeH.data.rt_exch,
              cd_io: focusedRowNodeH.data.cd_io,
              fg_slip: "2",
              tp_slip: "2",
              fg_io: focusedRowNodeH.data.fg_io,
              fg_money: focusedRowNodeH.data.fg_money,
              fg_trns: focusedRowNodeH.data.fg_trns,
              // cd_dept: focusedRowNodeH.data.cd_dept,
              cd_emp: codeEmp.code,
              // cd_gp: focusedRowNodeH.data.cd_gp,
              tp_trans: focusedRowNodeH.data.tp_trans,
              cd_pjt: focusedRowNodeH.data.cd_pjt,
              cd_mgmtdtl: focusedRowNodeH.data.cd_mgmtdtl,
              dc_rmk: focusedRowNodeH.data.dc_rmk,
              am: focusedRowNodeH.data.am,
              am_k: focusedRowNodeH.data.am_k,
              am_vat: focusedRowNodeH.data.am_vat,
              am_supply: focusedRowNodeH.data.am_supply,
            };

            qtioH.push(insertDataH);

            getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
              const insertDataL = {
                cd_firm: userInfo.cd_firm,
                no_io: "",
                no_line: node.data.no_line,
                cd_biz: focusedRowNodeH.data.cd_biz,
                cd_wh: codeWh.code ? codeWh.code : node.data.cd_wh,
                cd_io: focusedRowNodeH.data.cd_io,
                fg_slip: "2",
                tp_slip: "2",
                fg_io: focusedRowNodeH.data.fg_io,
                fg_money: focusedRowNodeH.data.fg_money,
                cd_item: node.data.cd_item,
                qt_good: node.data.qt,
                qt: node.data.qt,
                um: node.data.um,
                am: node.data.am,
                rt_exch: node.data.rt_exch,
                um_k: node.data.um_k,
                am_k: node.data.am_k,
                am_vat: node.data.am_vat,
                fg_vat: node.data.fg_vat,
                um_supply: node.data.um_supply,
                am_supply: node.data.am_supply,
                dt_rtn_plan: node.data.dt_rtn_plan,
                no_order: node.data.no_so,
                no_line_order: node.data.no_line_so,
                no_req: node.data.no_req,
                no_line_req: node.data.no_line,
                no_lc: node.data.no_lc,
                no_line_lc: node.data.no_line_lc,
                dc_rmk: node.data.dc_rmk,
                dt_io: dateBox,
                cd_pjt: focusedRowNodeH.data.cd_pjt,
              };
              qtio.push(insertDataL);
            });

            getGridCurrent("grid4").forEachNode((node: RowNode) => {
              if (node.data.no_req == focusedRowNodeH.data.no_req) {
                const qtioD = {
                  cd_firm: userInfo.cd_firm,
                  no_io: "",
                  no_line: node.data.no_line,
                  fg_slip: "2",
                  no_barcode: node.data.no_barcode,
                  cd_item: node.data.cd_item,
                  dt_io: dateBox,
                  cd_biz: focusedRowNodeH.data.cd_biz,
                  cd_wh: node.data.cd_wh,
                  cd_io: focusedRowNodeH.data.cd_io,
                  tp_slip: "2",
                  fg_io: focusedRowNodeH.data.fg_io,
                  fg_money: focusedRowNodeH.data.fg_money,
                  qt: node.data.qt_good,
                  qt_good: node.data.qt_good,
                  no_src: node.data.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  no_line_src: node.data.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
                  fg_unit: node.data.fg_unit,
                  no_ct: node.data.no_ct,
                  no_plt: node.data.no_plt,
                  dt_io_origin: node.data.dt_io_origin, // 원 LOT 입고일자
                  no_origin: node.data.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                  no_line_origin: node.data.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
                };
                qtioLotSn.push(qtioD);
              }
            });

            const cd_module: any = Menuprops.cd_module;
            const no_slip = await getAutoSlip(
              cd_module,
              8,
              dateBox,
              Menuprops.cd_menu
            );

            qtioH[0].no_io = no_slip;

            for (let row of qtio) {
              row.no_io = no_slip;
            }

            for (let row of qtioLotSn) {
              row.no_io = no_slip;
            }

            // 요청 데이터 구성
            const SaveData = {
              qtioh: { insert: qtioH },
              qtio: { insert: qtio },
              qtio_lotsn: { insert: qtioLotSn },
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_ISU_REG_004_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid1").resetUpdate();
            getGridCurrent("grid2").resetUpdate();
            getGridCurrent("grid3").resetUpdate();
            resetCondition();

            const mtl0011 = config?.MTL0011?.dc_ctrl ?? "";

            switch (mtl0011) {
              case "Y": {
                // 처리로 이동
                const currentSearch =
                  searchRef.current?.getFormdata() ?? formData;
                // 출하일로 덮어쓰기 전 조회조건 보관 (미처리 탭 복귀시 복원)
                prevSearchParams.current = { ...currentSearch };

                const isuDate = {
                  ...currentSearch,
                  dt_from: dateBox,
                  dt_to: dateBox,
                };
                setFormData(isuDate);

                setTabState({ page: "2", searchParams: isuDate });
                break;
              }
              default: // 재조회
                onSearch(null);
                break;
            }
          }
          break;
        case "Delete": // 취소
          {
            const focusedRowNodeH = getGridCurrent("grid5").focusedRowNode();
            if (!focusedRowNodeH) {
              return;
            }

            getGridCurrent("grid6").forEachFilteredNode((node: RowNode) => {
              if (node.data.cls > 0) {
                if (!hasInvalid) {
                  hasInvalid = true;
                }
              }
            });

            if (hasInvalid) {
              await showDialog(
                "alter",
                "마감 처리된 건이 존재하여 취소 할 수 없습니다"
              );
              return false;
            }

            const confirm = await showDialog("confirm", "취소하시겠습니까?");

            if (!confirm) {
              return; // 사용자가 아니오를 선택하면 함수 종료
            }

            getGridCurrent("grid7").applyFilters({
              no_io: focusedRowNodeH.data.no_io,
            });

            getGridCurrent("grid7").deleteAllRows();
            getGridCurrent("grid6").deleteAllRows();
            getGridCurrent("grid5").deleteSelectedRows();

            const qtiohChanges = getGridCurrent("grid5").getChange();
            const qtioChanges = getGridCurrent("grid6").getChange();
            const qtiolotsnChanges = getGridCurrent("grid7").getChange();

            // 요청 데이터 구성
            const SaveData = {
              qtioh: qtiohChanges,
              qtio: qtioChanges,
              qtio_lotsn: qtiolotsnChanges,
            };

            // 저장 API 호출
            const ok = await fnSave("SN_MTL_ISU_REG_004_SAVE_01", SaveData);
            if (!ok) return false;

            // 성공적으로 저장된 후 초기화
            getGridCurrent("grid5").resetUpdate();
            getGridCurrent("grid6").resetUpdate();
            getGridCurrent("grid7").resetUpdate();

            setTabState({
              page: "1",
              searchParams: restorePrevSearchParams(),
            });
          }
          break;
      }
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      getGridCurrent("grid5").resetUpdate(true);
      getGridCurrent("grid6").resetUpdate(true);
      getGridCurrent("grid7").resetUpdate(true);
    }
  };
  //#endregion

  //#region onPrint
  const onPrint = async () => {
    switch (tabPageState.page) {
      case "1": {
        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        if (focusedRowNodeH) {
          return {
            cd_firm: focusedRowNodeH.data.cd_firm,
            cd_biz: focusedRowNodeH.data.cd_biz,
            no_req: focusedRowNodeH.data.no_req,
          };
        } else {
          return;
        }
      }
      case "2": {
        const focusedRowNodeH = getGridCurrent("grid5").focusedRowNode();
        if (focusedRowNodeH) {
          const focusedRowNodeL = getGridCurrent("grid6").focusedRowNode();
          if (focusedRowNodeL) {
            return {
              cd_firm: focusedRowNodeH.data.cd_firm,
              cd_biz: focusedRowNodeH.data.cd_biz,
              no_req: focusedRowNodeL.data.no_req,
            };
          } else {
            return;
          }
        } else {
          return;
        }
      }
    }
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      // 거래구분이 조회 필수라 수불번호의 거래구분을 같이 넣는다(없으면 기본값 국내로 조회돼 0건).
      const fg_trns = await fnGetIoFgTrns(nodeData.no_io);
      const selectedData = {
        ...(fg_trns ? { fg_trns } : {}),
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      // 이미 "처리" 탭이면 page 가 그대로라 [tabPageState.page] effect 가 안 돈다 → 여기서 바로 재조회
      if (tabPageState.page === "2") onSearch(searchData);
      setTabState({ page: "2", searchParams: searchData });
    }
  };
  //#endregion

  //#region GridH Events
  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      // 출고창고가 선택돼있으면 해당 창고로 재고 조회
      if (codeWh.code !== "") {
        getInv(codeWh.code);
      }
    } else {
      return;
    }
  };
  //#endregion

  //#region GridL Events
  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "qt_good":
      case "qt_bad":
        rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad);
        CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], "rt_exch");
        break;
    }
  };

  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const cdWh = codeWh.code ? codeWh.code : selectedRow.cd_wh;
      getGridCurrent("grid3").applyFilters({
        cd_item: selectedRow.cd_item,
        cd_wh: cdWh,
        qt_inv: "notZero",
      });
      const qtRemain = selectedRow.qt - selectedRow.qt_isu;
      if (qtRemain > 0 && selectedRow.fg_unit !== "QT") {
        numberBoxFifoRef.current.setValue(qtRemain);
      } else {
        numberBoxFifoRef.current.setValue(0);
      }
    }
  };
  //#endregion

  //#region GridLotSn Events
  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "qt_isu") {
      if (rowData.qt_inv < rowData.qt_isu) {
        showDialog("alter", "재고량을 초과했습니다.");
        rowNode.setDataValue("qt_isu", 0);
      }
    }
  };

  const onRowDataUpdatedLotSn = () => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL) {
      txtBarcodeRef.current.focus();
    }
  };
  //#endregion

  //#region GridD Events
  const onBeforeAddRowD = (): any => {
    // 재고 선택하면 출고창고 못 바꾸게
    if (!whReadOnly) {
      setwhReadOnly(true);
    }
  };

  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        selectInv("C", "");
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
        const focusedRowNodeD = getGridCurrent("grid4").focusedRowNode();
        const cdWh = codeWh.code ? codeWh.code : focusedRowNodeL.data.cd_wh;

        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (
            node.data.cd_item === focusedRowNodeD.data?.cd_item &&
            node.data.cd_wh === focusedRowNodeD.data?.cd_wh &&
            node.data.no_barcode === focusedRowNodeD.data?.no_barcode &&
            node.data.dt_io === focusedRowNodeD.data?.dt_io_origin
          ) {
            node.setDataValue(
              "qt_inv",
              node.data.qt_inv + focusedRowNodeD.data?.qt_good
            );

            if (node.data.fg_unit === "SN") {
              node.setDataValue("qt_isu", 1);
            }

            focusedRowNodeL.setDataValue(
              "qt_isu",
              focusedRowNodeL.data?.qt_isu - focusedRowNodeD.data?.qt_good
            );
            return;
          }
        });
        getGridCurrent("grid4").deleteSelectedRows();
        getGridCurrent("grid3").applyFilters({
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: cdWh,
          qt_inv: "notZero",
        });

        if (getGridCurrent("grid4").getRowCount() < 1) {
          setwhReadOnly(false);
        }
        break;
    }
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
    const cdWh = codeWh.code ? codeWh.code : focusedRowNodeL.data.cd_wh;

    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: cdWh,
      qt_inv: "notZero",
    });

    const barcode = e;

    if (barcode === "" || barcode === null || barcode === undefined) {
      return;
    }

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      selectInv("S", barcode);
    } else {
      getGridCurrent("grid3").applyLikeFilters(
        {
          cd_item: focusedRowNodeL.data.cd_item,
          cd_wh: cdWh,
          qt_inv: "notZero",
          no_barcode: barcode,
          no_ct: barcode,
          no_plt: barcode,
        },
        ["no_barcode", "no_ct", "no_plt"]
      );
    }
  };
  //#endregion

  //#region GridH2 Events
  const onFocusedRowChangedH2 = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid6").selectAndFocusRow(0);
      setSelectedDocu(no_io);
    } else {
      return;
    }
  };
  //#endregion

  //#region onSnButtonClick
  const onSnButtonClick = async (id: string) => {
    switch (id) {
      case "Fifo":
        selectInv("F", "");
    }
  };
  //#endregion

  //#region Method
  const getInv = async (cdWh: string) => {
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    if (rowCount < 1) return;

    setDataSources((prev: any) => ({ ...prev, grid3: [] }));

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    const lotItemList: any = [];
    const snItemList: any = [];
    let invItemList: any = [];

    getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
      switch (node.data.fg_unit) {
        case "LOT":
          lotItemList.push({
            cd_biz: focusedRowNodeH.data.cd_biz,
            cd_item: node.data.cd_item,
            cd_wh: cdWh,
            cd_loc: "",
          });
          break;
        case "SN":
          snItemList.push({
            cd_biz: focusedRowNodeH.data.cd_biz,
            cd_item: node.data.cd_item,
            cd_wh: cdWh,
          });
          break;
      }
    });

    if (lotItemList.length > 0) {
      const searchLotData = await fnGetWhLocLotCtPltInv(lotItemList);
      const lotData = searchLotData.map((item: any) => {
        const { no_lot, ...rest } = item; // no_lot만 추출
        return {
          ...rest,
          no_barcode: no_lot, // 필드명 바꾸기
          fg_unit: "LOT", // LOT/SN구분
          qt_isu: 0, // 적용량 추가
        };
      });

      invItemList = [...invItemList, ...lotData];
    }

    if (snItemList.length > 0) {
      const searchSnData = await fnGetWhSnInv(snItemList);
      const snData = searchSnData.map((item: any) => {
        const { no_sn, ...rest } = item;
        return {
          ...rest,
          no_barcode: no_sn, // 필드명 바꾸기
          fg_unit: "SN", // LOT/SN구분
          no_src: item.no_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
          no_line_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
          qt_isu: 1, // 적용량 추가
          no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
          no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        };
      });

      invItemList = [...invItemList, ...snData];
    }

    setDataSources((prev: any) => ({ ...prev, grid3: invItemList }));

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
    getGridCurrent("grid3").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: cdWh,
      qt_inv: "notZero",
    });
  };

  const selectInv = (id: string, barcode: string) => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const cdWh = codeWh.code ? codeWh.code : focusedRowNodeL.data.cd_wh;

    let selectedMap = new Map<string, number>(); // 재고선택 키/수량 Map
    let qtAdd = 0,
      qtRemain = 0;

    switch (id) {
      case "F": // 선입선출
        if (numberBoxFifoRef.current.getValue == 0) {
          showDialog("alter", "수량이 입력되지 않았습니다.");
          return;
        }
        qtRemain = Number(numberBoxFifoRef.current.getValue);
        break;
      case "C": // 재고선택
        getGridCurrent("grid3").stopEditing();
        const selectedLotSn = getGridCurrent("grid3").getCheckedRows();
        if (selectedLotSn.length < 1) {
          showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        }

        selectedLotSn.forEach((row) => {
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          // 사용자가 그리드3에 입력한 '출고예정수량(qt_isu)'을 가져옴
          selectedMap.set(key, Number(row.qt_isu) || 0);
        });
        break;
      case "S": // 스캔
        // 요청량 있으면 요청량이 잔량
        qtRemain = focusedRowNodeL.data.qt - focusedRowNodeL.data.qt_isu;

        if (qtRemain === 0) {
          showDialog("alter", "의뢰수량이 다 등록되었습니다.");
          return false;
        }
        break;
    }

    getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;

      switch (id) {
        case "F": // 선입선출
          if (qtRemain <= 0) return;

          qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
          break;
        case "C": // 선택
          const key = `${row.no_barcode}_${row.no_ct}_${row.no_plt}_${row.dt_io}`;
          if (!selectedMap.has(key)) return;

          qtIsu = selectedMap.get(key) || 0; // 선택량 가져옴
          break;
        case "S": // 스캔
          // qtRemain === -1은 요청량 없는 건임 바코드찍은만큼 전체 출고
          if (
            String(row.no_barcode ?? "").toLowerCase() ===
              barcode.toLowerCase() ||
            String(row.no_ct ?? "").toLowerCase() === barcode.toLowerCase() ||
            String(row.no_plt ?? "").toLowerCase() === barcode.toLowerCase()
          ) {
            if (qtRemain !== -1) {
              if (qtRemain <= 0) return;

              qtIsu = Math.min(row.qt_inv, qtRemain); // 재고량이 적으면 재고량 아니면 출고잔량
            } else {
              qtIsu = row.qt_inv;
            }
          }
          break;
      }

      if (qtIsu <= 0) return;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeL.data?.cd_biz,
        no_req: focusedRowNodeL.data?.no_req,
        no_line: focusedRowNodeL.data?.no_line,
        dt_io: focusedRowNodeL.data?.dt_io,
        tp_slip: focusedRowNodeL.data?.tp_slip,
        fg_slip: focusedRowNodeL.data?.fg_slip,
        fg_io: focusedRowNodeL.data?.fg_io,
        fg_money: focusedRowNodeL.data?.fg_money,
        cd_io: focusedRowNodeL.data?.cd_io,
        cd_item: focusedRowNodeL.data?.cd_item,
        cd_wh: cdWh,
        no_barcode: node.data.no_barcode,
        fg_unit: node.data.fg_unit,
        qt_good: qtIsu,
        no_ct: node.data.no_ct,
        no_plt: node.data.no_plt,
        no_src: node.data.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_line_src: node.data.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        dt_io_origin: node.data.dt_io, // 원 LOT 입고일자
        no_origin: node.data.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_line_origin: node.data.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_batch: node.data.no_batch, // 26.11.02 추가
      };

      // qtRemain === -1은 요청량 없는 건
      if (qtRemain !== -1) {
        qtRemain -= qtIsu; // 잔량 빼주고
      }
      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0);
      node.setDataValue("qt_inv", node.data.qt_inv - qtIsu);

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
        if (
          node4.data.cd_item === focusedRowNodeL.data?.cd_item &&
          node4.data.cd_wh === cdWh &&
          node4.data.no_barcode === node.data.no_barcode &&
          node4.data.dt_io_origin === node.data.dt_io &&
          node4.data.no_ct === node.data.no_ct &&
          node4.data.no_plt === node.data.no_plt
        ) {
          node4.setDataValue("qt_good", node4.data.qt_good + qtIsu);
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid4").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue(
      "qt_isu",
      focusedRowNodeL.data?.qt_isu + qtAdd
    );
    numberBoxFifoRef.current.setValue(0);

    getGridCurrent("grid3").clearCheckBox();
    getGridCurrent("grid4").applyFilters({
      cd_item: focusedRowNodeL.data.cd_item,
      cd_wh: cdWh,
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };

  const resetCondition = (): any => {
    setLotSnData([]);
    setWhCode({ code: "", name: "" });
    setwhReadOnly(false);
  };
  //#endregion

  //#region onTabChanged
  // 처리 이동으로 덮어썼던 조회조건을 원래대로 되돌린다. 보관된 값이 없으면 null.
  const restorePrevSearchParams = (): any => {
    const saved = prevSearchParams.current;
    if (!saved) return null;

    prevSearchParams.current = null;
    setFormData(saved);
    return saved;
  };

  const onTabChanged = async (index: string) => {
    if (index === "1") {
      setTabState({ page: index, searchParams: restorePrevSearchParams() });
      return;
    }
    setTabState({ page: index, searchParams: null });
  };
  //#endregion

  //#region AddInfo
  const onExtraHeaderMenuClick = async (event: { id: string; params: any }) => {
    console.log(event.id);

    switch (event.id) {
      case "extra_item_setting1":
        await onOpenAddInfoPopup("grid2");
        break;
      case "extra_item_setting2":
        await onOpenAddInfoPopup("grid6");
        break;
    }
  };

  const onOpenAddInfoPopup = async (cdGrid: string) => {
    // id_target에 해당하는 현재 컬럼 메타를 설정 팝업으로 전달한다.
    const columnInfos =
      cdGrid === "grid2" ? columns.grid2 || [] : columns.grid6 || [];
    const saved = await openAddInfoPopup({
      cd_menu: Menuprops.cd_menu!,
      id_target: cdGrid,
      columnInfos,
    });

    if (saved) {
      const rtn: any = await getInitFormData();
      setFormControlData(rtn.formControlData);
      setFormData((prev: any) => ({
        ...(rtn.formData || {}),
        ...(prev || {}),
      }));
      setColums((prev: any) => ({
        ...prev,
        grid2: rtn.grid.grid2,
        grid6: rtn.grid.grid6,
      }));

      switch (cdGrid) {
        case "grid2":
          await loadAddInfoFlags(cdGrid, setAddInfoFlags);
          break;
        case "grid6":
          await loadAddInfoFlags(cdGrid, setAddInfoFlags2);
          break;
      }
      await onSearch(null);
    }
  };

  const loadAddInfoFlags = async (
    idTarget = "grid1",
    setFlags = setAddInfoFlags
  ) => {
    const result: any = await fnGetDataSet({
      id: "sys_addinfo_pop_01_01",
      params: {
        cd_firm: userInfo?.cd_firm ?? "",
        cd_menu: Menuprops?.cd_menu ?? "",
        id_target: idTarget,
      },
    });
    const rows = result?.[0] || [];
    const flags = extractAddInfoFlags(rows);
    setFlags(flags);
    return flags;
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onCellValueChanged: onCellValueChangedL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onExtraHeaderMenuClick: onExtraHeaderMenuClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onCellValueChanged: onCellValueChangedLotSn,
          onRowDataUpdated: onRowDataUpdatedLotSn,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: lotSnData,
        events: {
          onBeforeAddRow: onBeforeAddRowD,
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
      grid5: {
        columns: columns.grid5,
        dataSource: dataSources.grid5,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH2,
        },
      },
      grid6: {
        columns: columns.grid6,
        dataSource: dataSources.grid6,
        events: {
          onExtraHeaderMenuClick: onExtraHeaderMenuClick,
        },
      },
      grid7: {
        columns: columns.grid7,
        dataSource: dataSources.grid7,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnTab
          titles={["미처리", "처리"]}
          width={"100%"}
          selectedTab={tabPageState.page}
          onTabChanged={onTabChanged}
          header={[
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <div style={{ width: 60 }}>
                    <SnLabel text="출하일" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnDateBox
                      defaultValue={dateBox}
                      onValueChanged={dateValueChanged}
                    />
                  </div>
                  <div style={{ width: 70 }}>
                    <SnLabel text="출고창고" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnCodeBox
                      code="POPUP_WH"
                      isEditable={true}
                      isReadOnly={whReadOnly}
                      defaultValue={{
                        code: codeWh.code,
                        name: codeWh.name,
                      }}
                      onValueChanged={whValueChanged}
                    />
                  </div>
                  <div style={{ width: 60 }}>
                    <SnLabel text="담당자" type="string" />
                  </div>
                  <div style={{ width: 180 }}>
                    <SnCodeBox
                      code="POPUP_EMP"
                      defaultValue={{
                        code: codeEmp.code,
                        name: codeEmp.name,
                      }}
                      onValueChanged={empValueChanged}
                      isEditable={true}
                    />
                  </div>
                  <div style={{ paddingLeft: "80px" }}>
                    <SnButton
                      text={"처리"}
                      onClick={() => {
                        ExecuteRunner.run("Save");
                      }}
                    />
                  </div>
                </div>,
              ]}
            />,
            <SnTabHeader
              body={[
                <div className="relative flex space-x-2">
                  <SnButton
                    text={"취소"}
                    onClick={() => {
                      ExecuteRunner.run("Delete");
                    }}
                  />
                </div>,
              ]}
            />,
          ]}
          body={[
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
                <SnGrid
                  ref={gridRefs.current["grid1"]} // Grid Name
                  columnConfig={grids.grid1.columns} // 컬럼 설정 전달
                  rowData={grids.grid1.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  events={grids.grid1.events}
                  filter={{
                    refs: [gridRefs.current["grid2"]],
                    columns: ["no_req"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel2" orgSize="64.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
                    <SnGrid
                      ref={gridRefs.current["grid2"]} // Grid Name
                      columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                      rowData={grids.grid2.dataSource} // Datasource
                      etcProps={{
                        rowSelection: "multiple",
                        title: "상세정보",
                        extraHeaderMenuItems: [
                          { id: "extra_item_setting1", name: "부가항목 설정" },
                        ],
                      }}
                      events={grids.grid2.events}
                      filter={{
                        refs: [gridRefs.current["grid4"]],
                        columns: ["no_req", "no_line"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
                    <SnResizeBar direction={"height"} id="panel4" orgSize="50%">
                      <SnGrid
                        ref={gridRefs.current["grid3"]} // Grid Name
                        columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                        rowData={grids.grid3.dataSource} // Datasource
                        etcProps={{
                          rowSelection: "single",
                          title: "재고리스트",
                          ischeckboxSelection: true,
                        }}
                        events={grids.grid3.events}
                        header={
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <SnSwitch
                              ref={switchRef}
                              labelCheck="검색"
                              labelUncheck="스캔"
                              defaultChecked={false}
                            />
                            <div>
                              <SnBarcodeBox
                                onEnter={onBarcodeEnter}
                                inputRef={txtBarcodeRef}
                              />
                            </div>
                            <div className="w-[80px]">
                              <SnNumberBox
                                inputRef={numberBoxFifoRef}
                                customFormat="QT"
                              />
                            </div>
                            <div>
                              <SnButton
                                text="선입선출"
                                onClick={() => {
                                  onSnButtonClick("Fifo");
                                }}
                              />
                            </div>
                          </div>
                        }
                      />
                    </SnResizeBar>
                    <div className="flex-1 min-h-0">
                      <SnGrid
                        ref={gridRefs.current["grid4"]} // Grid Name
                        columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                        rowData={lotSnData} // Datasource
                        etcProps={{ rowSelection: "single", title: "출고정보" }}
                        events={grids.grid4.events}
                      />
                    </div>
                  </div>
                </div>
              </SnResizeBar>
            </div>,
            <div className="flex-1 h-full min-h-0 space-y-1.5">
              <SnResizeBar direction={"height"} id="panel5" orgSize="35.5%">
                <SnGrid
                  ref={gridRefs.current["grid5"]} // Grid Name
                  columnConfig={grids.grid5.columns} // 컬럼 설정 전달
                  rowData={grids.grid5.dataSource} // Datasource
                  etcProps={{ rowSelection: "single" }}
                  events={grids.grid5.events}
                  filter={{
                    refs: [gridRefs.current["grid6"]],
                    columns: ["no_io"],
                  }}
                />
              </SnResizeBar>
              <SnResizeBar direction={"height"} id="panel6" orgSize="55.5%">
                <div className="flex w-full h-full min-w-0 space-x-1.5">
                  <SnResizeBar direction={"width"} id="panel7" orgSize="60%">
                    <SnGrid
                      ref={gridRefs.current["grid6"]} // Grid Name
                      columnConfig={grids.grid6.columns} // 컬럼 설정 전달
                      rowData={grids.grid6.dataSource} // Datasource
                      etcProps={{
                        rowSelection: "multiple",
                        title: "상세정보",
                        extraHeaderMenuItems: [
                          { id: "extra_item_setting2", name: "부가항목 설정" },
                        ],
                      }}
                      events={grids.grid6.events}
                      filter={{
                        refs: [gridRefs.current["grid7"]],
                        columns: ["no_io", "no_line"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
                    <SnGrid
                      ref={gridRefs.current["grid7"]} // Grid Name
                      columnConfig={grids.grid7.columns} // 컬럼 설정 전달
                      rowData={grids.grid7.dataSource} // Datasource
                      etcProps={{ rowSelection: "multiple", title: "출하정보" }}
                    />
                  </div>
                </div>
              </SnResizeBar>
              <SnFileUploadBox
                selectedDocu={selectedDocu}
                selectedLineDocu={0}
              />
            </div>,
          ]}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_ISU_REG_004;
